// chill-color-absorb: site-wide implementation of MASTER-BRIEF §6 Law 2
// "Color follows the product".
//
// Sprint 2.5 polish. The product card already extracts dominant color
// per-item and writes --chill-card-glow. This module promotes that local
// signal to a SITE-WIDE ambient tint:
//
//   - Hovering a chill-product-card → page background subtly absorbs the
//     extracted color (var(--chill-glow) gets a 4% tint applied to body)
//   - Pointer-leave → fades back to neutral over 800ms
//   - Active product on PDP → ambient tint persists while gallery is
//     visible (uses the same var(--chill-glow) the card already sets)
//
// The effect is small (4% mix) — felt, not seen. Brand promise: "background
// subtly shifts to complement the active product".
//
// Spec: docs/MASTER-BRIEF.md §6 Law 2, §7.1 chill-color-absorb
// Budget: < 0.7 KB gzip. No deps. CSS does the actual coloring; JS just
// updates the timing.

(function () {
  'use strict';

  if (window.chillandoutColorAbsorbLoaded) return;
  window.chillandoutColorAbsorbLoaded = true;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  let releaseTimer = 0;
  const RELEASE_MS = 800;

  function onCardEnter(e) {
    const card = e.target.closest('chill-product-card');
    if (!card) return;
    if (releaseTimer) clearTimeout(releaseTimer);
    document.documentElement.dataset.chillAbsorbActive = 'true';
  }

  function onCardLeave(e) {
    const card = e.target.closest('chill-product-card');
    if (!card) return;
    if (releaseTimer) clearTimeout(releaseTimer);
    releaseTimer = setTimeout(() => {
      document.documentElement.dataset.chillAbsorbActive = 'false';
      // Reset --chill-glow on root so neutral state holds.
      document.documentElement.style.removeProperty('--chill-glow');
    }, RELEASE_MS);
  }

  // Delegated listeners — covers cards loaded via Section Rendering API.
  document.addEventListener('pointerenter', onCardEnter, true);
  document.addEventListener('pointerleave', onCardLeave, true);

  /* ---------- PDP active-image absorb ----------
   *
   * On product templates, the gallery image's dominant color drives the
   * page tint. We sample the first visible image once on load and write
   * --chill-glow to root. No re-sample per slide change for now (the
   * carousel-driven sample is a polish++ later).
   */

  function maybePdpAbsorb() {
    if (document.body?.dataset.chillTemplate?.indexOf('product') !== 0) {
      // Only on product templates.
      const tpl = (document.body && document.body.className) || '';
      if (!/template-product\b|\bpage-product\b/.test(tpl)) return;
    }
    // Horizon uses <media-gallery> wrapper; fall back to legacy classes.
    const img = document.querySelector(
      'media-gallery img, .media-gallery__grid img, .product-media img, .product-media-gallery img'
    );
    if (!img) return;
    if (!img.complete || img.naturalWidth === 0) {
      img.addEventListener('load', () => sampleAndApply(img), { once: true });
      return;
    }
    sampleAndApply(img);
  }

  function sampleAndApply(img) {
    try {
      const c = document.createElement('canvas');
      c.width = 2; c.height = 3;
      const ctx = c.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(img, 0, 0, 2, 3);
      const { data } = ctx.getImageData(0, 0, 2, 3);
      let r = 0, g = 0, b = 0, n = 0;
      for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] < 16) continue;
        r += data[i]; g += data[i + 1]; b += data[i + 2]; n++;
      }
      if (!n) return;
      const rgba = `rgba(${Math.round(r/n)},${Math.round(g/n)},${Math.round(b/n)},0.32)`;
      document.documentElement.style.setProperty('--chill-glow', rgba);
      document.documentElement.dataset.chillAbsorbActive = 'true';
    } catch {
      // CORS taint or canvas error — silent fallback to default.
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', maybePdpAbsorb, { once: true });
  } else {
    maybePdpAbsorb();
  }
})();
